import { JSONParser, ParsedElementInfo } from '@streamparser/json';
import { useEditorLanguages } from 'articleEditor/components/localePicker/useLocales';
import { ArticleSavingContext } from 'articleEditor/contexts/savingContext';
import { atom, useSetAtom } from 'jotai';
import { useMyEditorRef } from 'lib-knowledge-document-structure/plateHooks';
import {
  MyRootBlock,
  MyValue,
} from 'lib-knowledge-document-structure/plateTypes';
import { LanguageContext } from 'lib-knowledge-management-common/contexts/language';
import { LanguageModel } from 'lib-knowledge-management-common/hooks/useManagementLanguages';
import {
  TranslatingStatus,
  TranslatingType,
} from 'lib-knowledge-remote-types/general.types';
import { useNotification } from 'lib-knowledge-utils/hooks/useNotification';
import {
  ApiRequest,
  FetcherExtraArgs,
  fetcherFunction,
} from 'lib-knowledge-utils/restClient';
import { useContext } from 'react';
import useSWRMutation from 'swr/mutation';

type Article = {
  title?: string;
  content?: MyRootBlock[];
};

type TranslationRequest = {
  translationLanguage: string;
  defaultLanguage: string;
  article: Article;
};

export const translatingStatusAtom = atom<TranslatingStatus>({
  type: TranslatingType.NONE,
});

const CHUNK_CHARACTER_LIMIT = 10000;

/**
 * Splits an array of content blocks into chunks where each chunk's JSON
 * stringified content is within the character limit. If a single block exceeds
 * the limit, it will be placed in its own chunk.
 */
const splitContentIntoChunks = (
  content: MyRootBlock[],
  characterLimit: number,
): MyRootBlock[][] => {
  const chunks: MyRootBlock[][] = [];
  let currentChunk: MyRootBlock[] = [];
  let currentChunkSize = 0;

  content.forEach((block) => {
    const blockSize = JSON.stringify(block).length;

    if (
      currentChunkSize + blockSize > characterLimit &&
      currentChunk.length > 0
    ) {
      chunks.push(currentChunk);
      currentChunk = [block];
      currentChunkSize = blockSize;
    } else {
      currentChunk.push(block);
      currentChunkSize += blockSize;
    }
  });

  // Adding the last chunk to the chunks array
  if (currentChunk.length > 0) {
    chunks.push(currentChunk);
  }

  return chunks;
};

type UseBaseTranslationProps = {
  translatingType: TranslatingType;
  onParsedBlock?: (
    block: MyRootBlock,
    setChildren: (children: MyValue) => void,
  ) => void;
};

type UseBaseTranslationReturn = {
  translate: (article: Article) => Promise<void>;
  setGlobalLoading: (loading: boolean, blockId?: string) => void;
  clearChildren: () => void;
  clearTitle: () => Promise<void>;
  stopLoadingAndShowErrorNotification: () => void;
  currentLanguage: LanguageModel | undefined;
};

/**
 * This hook is used only to share functionality between all the other
 * translation hooks.
 *
 * @param translationType - The type of the translation
 * @param onParsedBlock - A function that is called when a block has been parsed
 */
export const useBaseTranslation = ({
  translatingType,
  onParsedBlock,
}: UseBaseTranslationProps): UseBaseTranslationReturn => {
  const editor = useMyEditorRef();
  const setTranslatingStatus = useSetAtom(translatingStatusAtom);
  const { defaultLanguage, translatedLanguages } = useEditorLanguages();
  const { actions } = useContext(ArticleSavingContext);
  const { currentLanguage: currentLanguageId } = useContext(LanguageContext);
  const currentLanguage = translatedLanguages.find(
    (language) => language.languageId === currentLanguageId,
  );
  const { successNotify, errorNotify } = useNotification();
  const { trigger: fetchTranslation } = useSWRMutation<
    Article,
    Error,
    ApiRequest,
    FetcherExtraArgs<TranslationRequest>
  >(
    {
      path: 'v4/ai/text/translate-article',
      params: {
        stream: true,
      },
      method: 'POST',
    },
    fetcherFunction,
  );

  const setGlobalLoading = (loading: boolean, blockId?: string) => {
    const translatingStatus = loading
      ? { type: translatingType, blockId }
      : { type: TranslatingType.NONE };
    setTranslatingStatus(translatingStatus);
    editor.plate.set.readOnly(loading);
    actions.setTranslatingStatus(translatingStatus);
  };

  const setChildren = (children: MyValue) => {
    /**
     * Before setting the children we need to reset the state as specified in
     * the documentation https://platejs.org/docs/controlled. I tried to also
     * clean the children and use `editor.insertNodes` when translating the
     * whole article, but it sometimes inserts empty blocks for no reason
     */
    editor.reset();
    editor.children = children;
  };

  const setTitle = async (title?: string) => {
    if (title === undefined) {
      return;
    }

    await actions.setTitle(title);
  };

  const clearChildren = () => setChildren([]);

  const clearTitle = () => setTitle('');

  const stopLoadingAndShowErrorNotification = () => {
    setGlobalLoading(false);
    errorNotify({
      title: 'Error',
      text: `Translation failed. Please try again`,
    });
  };

  /**
   * We split the translation into chunks to avoid hitting LLM tokens
   * input/output limits, so we define a function that handles the streaming of
   * each chunk
   */
  const getStreamingFunction =
    (
      parser: JSONParser,
      chunkIndex: number,
      processNextChunk: (index: number) => Promise<boolean>,
    ) =>
    async (response: Response) => {
      const reader = response.body?.getReader();
      if (reader) {
        const processChunk = async ({
          done,
          value,
        }: ReadableStreamReadResult<Uint8Array | undefined>) => {
          value && parser.write(value);
          if (done) {
            const hasNextChunk = await processNextChunk(chunkIndex + 1);
            if (!hasNextChunk) {
              setGlobalLoading(false);
              successNotify({
                title: 'Success',
                text: `Translation complete`,
              });
            }
          } else {
            reader.read().then(processChunk);
          }
        };
        const { done, value } = await reader.read();
        processChunk({ done, value });
      } else {
        stopLoadingAndShowErrorNotification();
      }
    };

  const parserOnValue = async (
    parsedElementInfo: ParsedElementInfo.ParsedElementInfo,
  ) => {
    try {
      if (
        parsedElementInfo.key === 'title' &&
        typeof parsedElementInfo.value === 'string'
      ) {
        await setTitle(parsedElementInfo.value);
      } else {
        const block = parsedElementInfo.value as MyRootBlock;
        onParsedBlock?.(
          {
            ...block,
            translatedAt: new Date().toISOString(),
          },
          setChildren,
        );
      }
    } catch {
      stopLoadingAndShowErrorNotification();
    }
  };

  const translate = async (article: Article): Promise<void> => {
    if (!currentLanguage) {
      return;
    }

    const chunks = article.content
      ? splitContentIntoChunks(article.content, CHUNK_CHARACTER_LIMIT)
      : /**
         * If there's no content (when translating only the title), we still need to
         * have at least one chunk in the array to enter the for loop below, so we use
         * an array with one undefined element that will be the equivalent of having no
         * content
         */
        [undefined];

    const getTranslationRequestForChunk = async (
      index: number,
    ): Promise<void> => {
      const parser = new JSONParser({
        /**
         * Only parse title when processing the first chunk (we include it in
         * every chunk request for context) so we don't translate it multiple
         * times
         */
        paths: index === 0 ? ['$.title', '$.content.*'] : ['$$.content.*'],
        keepStack: false,
      });

      parser.onValue = parserOnValue;

      await fetchTranslation({
        body: {
          translationLanguage: currentLanguage.description,
          defaultLanguage: defaultLanguage.description,
          article: {
            // always send the title for context
            title: article.title,
            content: chunks[index],
          },
        },
        streamingFunction: getStreamingFunction(
          parser,
          index,
          processNextChunk,
        ),
      });
    };

    const processNextChunk = async (index: number): Promise<boolean> => {
      if (index >= chunks.length) {
        return false;
      }
      await getTranslationRequestForChunk(index);
      return true;
    };

    await getTranslationRequestForChunk(0);
  };

  return {
    translate,
    setGlobalLoading,
    clearChildren,
    clearTitle,
    stopLoadingAndShowErrorNotification,
    currentLanguage,
  };
};
